import { useState } from "react";
import axios from "axios";
import "./App.css";

export default function App() {
  const [address, setAddress] = useState("");
  const [borough, setBorough] = useState("Brooklyn");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const boroughs = ["Brooklyn", "Manhattan", "Queens", "Bronx", "Staten Island"];

  const lookup = async () => {
    if (!address.trim()) return;
    
    setLoading(true);
    setData(null); // Clear previous data
    try {
      const fullAddress = `${address} ${borough}`;
      const params = new URLSearchParams({
        address: fullAddress,
        strategy: 'standard',
        pretty: 'true'
      });

      const res = await axios.get(`/report?${params}`);
      setData(res.data);
    } catch (err) {
      console.error(err);
      alert("Error fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      lookup();
    }
  };

  // Function to format the output text for better readability
  const formatOutput = (text) => {
    if (!text) return '';

    // Handle different line break types and normalize
    const normalizedText = text
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n')
      .replace(/\\n/g, '\n');
    
    // Remove leading emoji/pictograph markers used by the backend for line typing.
    const stripEmoji = (s) =>
      s.replace(/^[\s]*(?:[\p{Extended_Pictographic}☀-➿️‍⃣]+[\s]*)+/u, '').trim();

    const renderLine = (line, index) => {
        const trimmedLine = line.trim();
        const cleanLine = line.replace(/[""]/g, '"').replace(/['']/g, "'");
        const noHtml = cleanLine.replace(/<[^>]+>/g, '');
        const bare = stripEmoji(noHtml);
        const indented = /^(\s{3,}|\t)/.test(cleanLine);

        // --- Tenant-union call to action (backend sentinel) ---
        if (trimmedLine.startsWith('✊TENANTUNION|')) {
          const reason = trimmedLine.split('|').slice(1).join('|').trim();
          const talkToTenants = 'https://www.nyc.gov/content/organize/pages/talk-to-tenants';
          return (
            <div key={index} className="union-cta">
              <div className="union-cta-head">✊ Start a tenant union</div>
              <div className="union-cta-body">{reason}</div>
              <div className="union-cta-note">
                NYC's new <strong>Talk to Tenants</strong> program helps renters in
                buildings like this organize. It's free and no experience is needed.
              </div>
              <div className="union-cta-actions">
                <a className="union-cta-btn" href={talkToTenants} target="_blank" rel="noopener noreferrer">
                  Start a tenant union&nbsp;→
                </a>
                <a className="union-cta-link" href={talkToTenants} target="_blank" rel="noopener noreferrer">
                  Free NYC 101 organizing workshops
                </a>
              </div>
            </div>
          );
        }

        // --- Collapsible "what this means" note (backend sentinel) ---
        if (trimmedLine.startsWith('▸MORE|')) {
          const parts = trimmedLine.slice('▸MORE|'.length).split('|');
          const summary = (parts.shift() || 'What this means').trim();
          const body = parts.join('|').trim();
          return (
            <details key={index} className="more-note">
              <summary className="more-summary">{summary}</summary>
              <div className="more-body">{body}</div>
            </details>
          );
        }

        // --- Major section headers ---
        if (/RENT STABILIZATION EDUCATED GUESS/i.test(bare)) {
          return <div key={index} className="section-header">Rent Stabilization Estimate</div>;
        }
        if (/TENANT EMPOWERMENT ANALYSIS/i.test(bare)) {
          return <div key={index} className="empowerment-header">Tenant Empowerment Analysis</div>;
        }
        // Sub-section: a scored child analysis (backend prefixes it with a status
        // emoji). One level DOWN from the primary section it lives under.
        if (!indented && /^\s*[🟢🟡🔴]/.test(trimmedLine) && /Analysis\s*$/i.test(bare)) {
          return <div key={index} className="subsection-header">{bare}</div>;
        }
        // Primary section titles — these break the report into scannable sections
        if (!indented && (
              /^[A-Za-z][A-Za-z /&'-]{2,40} Analysis:?$/i.test(bare) ||
              /^Data Sources Analyzed:?$/i.test(bare) ||
              /^Rent Stabilization Basis:?$/i.test(bare) ||
              /^AI-Simplified [A-Za-z/ ]*Violations?:?$/i.test(bare) ||
              /^Open Complaints:?$/i.test(bare))) {
          return <div key={index} className="section-header">{bare.replace(/:$/, '')}</div>;
        }

        // --- Confidence bar: real colored meter, not grey ASCII ---
        const confMatch = bare.match(/^Confidence:\s*(\d{1,3})\s*%/i);
        if (confMatch) {
          const pct = Math.max(0, Math.min(100, parseInt(confMatch[1], 10)));
          const level = pct >= 70 ? 'good' : pct >= 40 ? 'caution' : 'danger';
          return (
            <div key={index} className="confidence">
              <span className="confidence-label">Confidence</span>
              <span className="confidence-track">
                <span className={`confidence-fill ${level}`} style={{ width: pct + '%' }}></span>
              </span>
              <span className={`confidence-pct ${level}`}>{pct}%</span>
            </div>
          );
        }

        // --- Numbered violation item: "1. ✅🟢 Resolved" -> number + dot + word ---
        const numMatch = noHtml.match(/^\s*(\d+)\.\s*(.+)$/);
        if (numMatch && !indented && /✅|🟢|🟡|🔴|resolved|open|active|closed/i.test(numMatch[2])) {
          const restBare = stripEmoji(numMatch[2]);
          const resolved = /resolved|closed|fixed/i.test(numMatch[2]) || /✅|🟢/.test(numMatch[2]);
          const open = !resolved && /open|active|🔴/i.test(numMatch[2]);
          const dot = resolved ? 'good' : open ? 'danger' : 'caution';
          return (
            <div key={index} className="violation-item">
              <span className="violation-num">{numMatch[1]}</span>
              <span className={`status-dot ${dot}`} aria-hidden="true"></span>
              <span className="violation-status">{restBare || (resolved ? 'Resolved' : 'Open')}</span>
            </div>
          );
        }

        // --- Field line: "Label: value" (the workhorse) ---
        const isFieldCandidate =
          !indented &&
          /^[^:]{1,32}:/.test(bare) &&
          (trimmedLine.startsWith('🏠') || trimmedLine.startsWith('👤') || trimmedLine.startsWith('🏢') ||
           trimmedLine.startsWith('🧩') || trimmedLine.startsWith('💰') || trimmedLine.startsWith('📜') ||
           trimmedLine.startsWith('📅') || trimmedLine.startsWith('🏗️') || trimmedLine.startsWith('📋') ||
           trimmedLine.startsWith('📝') || trimmedLine.startsWith('🍵') || trimmedLine.startsWith('🤖'));
        if (isFieldCandidate) {
          const ci = bare.indexOf(':');
          const label = bare.substring(0, ci).trim();
          const value = bare.substring(ci + 1).trim();

          // ADDRESS is the hero of the whole report.
          if (/^address$/i.test(label)) {
            return (
              <div key={index} className="report-hero">
                <div className="report-kicker">Report for</div>
                <div className="report-address">{value}</div>
              </div>
            );
          }

          // Confirmed rent-stabilization verdict — the answer, as a callout.
          if (/^rent stabiliz/i.test(label) && /confirm/i.test(value)) {
            return (
              <div key={index} className="verdict-confirmed">
                <span className="verdict-badge">Confirmed</span>
                <span className="verdict-text">Rent Stabilized</span>
              </div>
            );
          }

          // If there's no value, treat it as a section title (e.g. "AI-Simplified Violations:")
          if (!value) {
            return <div key={index} className="section-header">{label}</div>;
          }

          // Shell-risk gets a semantic chip.
          if (/shell.?risk/i.test(label)) {
            const level = /high/i.test(value) ? 'high' : /med/i.test(value) ? 'med' : 'low';
            return (
              <div key={index} className="data-row key">
                <span className="data-label">{label}</span>
                <span className={`risk-chip ${level}`}>{value}</span>
              </div>
            );
          }

          // Verdict fields pop; detail fields stay quiet.
          const keyField = /^(owner|owners|owner\(s\)|shell corporations|rent stabilized|basis)$/i.test(label);
          return (
            <div key={index} className={`data-row ${keyField ? 'key' : ''}`}>
              <span className="data-label">{label}</span>
              <span className={`data-value ${keyField ? 'data-value-strong' : ''}`}>{value}</span>
            </div>
          );
        }

        // --- Status lines -> semantic dot + text (never color alone) ---
        if (trimmedLine.startsWith('✅') || trimmedLine.startsWith('🟢')) {
          return <div key={index} className="status-line"><span className="status-dot good" aria-hidden="true"></span>{bare}</div>;
        }
        if (trimmedLine.startsWith('❓') || trimmedLine.startsWith('🟡')) {
          return <div key={index} className="status-line"><span className="status-dot caution" aria-hidden="true"></span>{bare}</div>;
        }
        if (trimmedLine.startsWith('🔴')) {
          return <div key={index} className="status-line"><span className="status-dot danger" aria-hidden="true"></span>{bare}</div>;
        }
        if (trimmedLine.startsWith('⚠️') || trimmedLine.startsWith('🚨')) {
          return <div key={index} className="warning-line">{bare}</div>;
        }

        // --- Expand/collapse for long complaint text ("💬") ---
        if (trimmedLine.startsWith('💬')) {
          const text = bare;
          const max = 140;
          if (text.length > max) {
            return (
              <details key={index} className="regular-line">
                <summary>{text.slice(0, max - 1) + '…'}</summary>
                <div className="regular-line">{text}</div>
              </details>
            );
          }
          return <div key={index} className="regular-line">{text}</div>;
        }

        // --- Supporting detail / evidence (indented) ---
        if (indented) {
          return <div key={index} className="indented-line">{bare}</div>;
        }

        return <div key={index} className="regular-line">{bare}</div>;
    };

    // Fold "▸YEAR 2026 (1)" markers + their following indented lines into
    // collapsible year groups so closed-violation history doesn't wall the page.
    const rawLines = normalizedText.split('\n').filter((l) => l.trim() !== '');
    const out = [];
    let i = 0;
    while (i < rawLines.length) {
      const ym = rawLines[i].trim().match(/^▸YEAR\s+(.+)$/);
      if (ym) {
        const body = [];
        i++;
        while (i < rawLines.length && /^(\s{2,}|\t)/.test(rawLines[i]) && !rawLines[i].trim().startsWith('▸YEAR')) {
          body.push(rawLines[i]);
          i++;
        }
        const groupKey = 'yg' + i;
        out.push(
          <details key={groupKey} className="year-group">
            <summary className="year-summary">{ym[1]}</summary>
            <div className="year-body">{body.map((b, bi) => renderLine(b, groupKey + '_' + bi))}</div>
          </details>
        );
        continue;
      }
      out.push(renderLine(rawLines[i], i));
      i++;
    }
    return out;
  };



  return (
    <div className="app">
      {/* Header */}
      <div className="header">
        <div className="container">
          <div className="title-container">
            <img src="/logo.png" alt="Logo" className="title-logo" />
            <h1 className="title">SLUMLORD SNITCH</h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        
        {/* Glowing Search Section */}
        <div className="search-section">
          <div className="search-form">
            
            {/* Address Input - GLOWING */}
            <div className="input-group">
              <input
                type="text"
                placeholder="Enter address (e.g., 1234 Park Ave)..."
                value={address}
                onChange={(e) => {
                  setAddress(e.target.value);
                  if (data) setData(null); // Clear data when user starts typing
                }}
                onKeyPress={handleKeyPress}
                className="address-input"
              />
            </div>

            {/* Borough Selection */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label" htmlFor="borough">Borough</label>
              <select
                id="borough"
                value={borough}
                onChange={(e) => setBorough(e.target.value)}
                className="form-select"
              >
                {boroughs.map(b => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
          </div>

            {/* Search Button */}
            <button
              onClick={lookup}
              disabled={loading || !address.trim()}
              className="search-button"
            >
              {loading ? 'Snitching…' : 'Start snitching'}
            </button>
          </div>
        </div>

        {/* Results Terminal */}
        {data && (
          <div className="results-grid">
            {/* Disclaimer banner (only when backend signals it) */}
            {data.disclaimerNote && (
              <div className="disclaimer-banner">
                {data.disclaimerNote}
              </div>
            )}
            
            {/* Report Output */}
            <div className="terminal-output">
              <div className="terminal-content">
                <div className="formatted-output">
                  {formatOutput(typeof data === 'string' ? data : (data.prettyOutput || JSON.stringify(data, null, 2)))}
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Data Sources & Methodology */}
        <div className="methodology">
          <h2 className="methodology-title">Data Sources &amp; Methodology</h2>
          <div className="methodology-grid">
            <div className="method-group">
              <h3 className="method-heading">NYC public records</h3>
              <ul>
                <li><strong>HPD violations &amp; complaints</strong> — NYC Housing Preservation &amp; Development</li>
                <li><strong>DOB violations &amp; complaints</strong> — NYC Department of Buildings</li>
                <li><strong>OATH / ECB violations</strong> — Office of Administrative Trials &amp; Hearings</li>
                <li><strong>311 service requests</strong> — live tenant complaints</li>
                <li><strong>PLUTO &amp; ACRIS</strong> — property characteristics &amp; ownership records</li>
                <li><strong>DOF property exemptions</strong> — 421-a / J-51 tax-benefit records</li>
              </ul>
            </div>
            <div className="method-group">
              <h3 className="method-heading">How we analyze</h3>
              <ul>
                <li><strong>Rent stabilization</strong> — confirmed from active 421-a / J-51 tax exemptions on record with NYC Finance (validated, not estimated), and further public sources</li>
                <li><strong>Ownership &amp; shell-risk</strong> — HPD registration contacts, cross-referenced for shell-LLC patterns</li>
                <li><strong>Violation plain-language</strong> — summarized by Google Gemini</li>
                <li><strong>Rent withholding</strong> — HPD rent-impairing designations + NY Multiple Dwelling Law §302-a</li>
              </ul>
            </div>
          </div>
          <p className="methodology-note">Plain-language summaries powered by Google Gemini · built on NYC public data.</p>
        </div>

        {/* Footer */}
        <div className="footer">
          <p>For informational purposes only • Not legal advice • Consult an attorney for legal matters</p>
          <p>Built to expose slumlords and protect tenants</p>
        </div>
      </div>
    </div>
  );
}